import * as React from 'react';
import React__default, { createContext, useMemo, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { a as __assign, b as __spreadArray, _ as __rest } from './tslib.es6-ff7ae7b8.js';
import * as require$$2 from 'react-dom';
import { c as classNames } from './index-b0444989.js';
import { animated, useTransition } from 'react-spring';
import './countries-0aa398a1.js';
import { IconButton } from './es/components/IconButton.js';
import { textLinkStyles } from './es/components/TextLink.js';
import { Typography } from './es/components/Typography.js';
import { R as ResizeObserver } from './ResizeObserver-67118f52.js';
import { C as CircleAlertIcon } from './CircleAlertIcon-260aea89.js';
import { C as CloseIcon } from './CloseIcon-ee1d8a9a.js';
import { I as InfoIcon } from './InfoIcon-a2368fdb.js';
import { T as ThemeProvider } from './ThemeProvider-22ee39e3.js';

function createPortalEl(id) {
    var el = document.createElement("div");
    el.setAttribute("id", id);
    return el;
}
function addPortalEl(el, appRootId) {
    var _a;
    (_a = document.getElementById(appRootId)) === null || _a === void 0 ? void 0 : _a.appendChild(el);
}
var usePortal = function (id, className, style, active) {
    if (active === void 0) { active = true; }
    var portalRef = React.useRef(null);
    var appId = useOrchard().appId;
    React.useEffect(function () {
        if (!active)
            return;
        var existingElement = document.getElementById(id);
        var parentElement = existingElement || createPortalEl(id);
        if (!existingElement) {
            addPortalEl(parentElement, appId);
            parentElement.appendChild(portalRef.current);
        }
        var resolvedElement = document.getElementById(id);
        if (resolvedElement) {
            if (className)
                resolvedElement.classList.add("class", className || "");
            resolvedElement.style.cssText += reactStyleToCssStyleString(__assign({}, style));
        }
    }, [active, appId, className, id, style]);
    // Called on destroy only, cant have dep's
    React.useEffect(function () { return function () {
        var _a;
        (_a = document.getElementById(id)) === null || _a === void 0 ? void 0 : _a.remove();
    }; }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    React.useEffect(function () {
        var _a, _b;
        if (!active) {
            (_a = portalRef.current) === null || _a === void 0 ? void 0 : _a.remove();
            (_b = document.getElementById(id)) === null || _b === void 0 ? void 0 : _b.remove();
        }
    }, [active, id]);
    function getPortalEl() {
        if (portalRef.current === null) {
            portalRef.current = document.createElement("div");
        }
        return portalRef.current;
    }
    return getPortalEl();
};
function reactStyleToCssStyleString(styleObj) {
    var elm = new Option();
    Object.entries(styleObj).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        elm.style[key] = value;
    });
    return elm.getAttribute("style") || "";
}

var OrchardPortal = React.forwardRef(function OrchardPortal(props, ref) {
    var children = props.children, className = props.className, portalClassName = props.portalClassName, style = props.style, active = props.active;
    var portalTarget = usePortal(props.portalId, className, style, active);
    var content = (React.createElement("div", { ref: ref, className: portalClassName }, children));
    return require$$2.createPortal(content, portalTarget);
});

// Consume in your app
var STACK_ID_BASE = "orc-stack";
var StackingContext = createContext(null);
var StackingContextProvider = function (_a) {
    var noPortal = _a.noPortal, className = _a.className, style = _a.style, id = _a.id, children = _a.children, _b = _a.portalActive, portalActive = _b === void 0 ? true : _b;
    var stackId = useMemo(function () { return id || "".concat(STACK_ID_BASE, "-").concat(Math.random().toString(36).slice(-6)); }, [id]);
    if (id || noPortal) {
        return (React__default.createElement(StackingContext.Provider, { value: { stackId: stackId } },
            React__default.createElement("div", { className: className, style: style, id: stackId }, children)));
    }
    return (React__default.createElement(StackingContext.Provider, { value: { stackId: stackId } },
        React__default.createElement(OrchardPortal, { portalId: stackId, portalClassName: className, style: __assign({ position: "absolute", height: 0 }, style), active: portalActive }, children)));
};
var useStackingContext = function () {
    var context = useContext(StackingContext);
    if (!context) {
        throw new Error("useStackingContext must be used within a StackingContextProvider. It is likely that you forgot to wrap your component in an OrchardProvider.");
    }
    return { stackElementId: context.stackId };
};

var styles = {"container":"Toast_container_701803f7","toastWrapper":"Toast_toastWrapper_701803f7","toast":"Toast_toast_701803f7","small":"Toast_small_701803f7","medium":"Toast_medium_701803f7","large":"Toast_large_701803f7","info":"Toast_info_701803f7","success":"Toast_success_701803f7","warning":"Toast_warning_701803f7","error":"Toast_error_701803f7","action":"Toast_action_701803f7","content":"Toast_content_701803f7","body":"Toast_body_701803f7","title":"Toast_title_701803f7","subText":"Toast_subText_701803f7","icon":"Toast_icon_701803f7","ctasContainer":"Toast_ctasContainer_701803f7","cta":"Toast_cta_701803f7","primary":"Toast_primary_701803f7","closeBtn":"Toast_closeBtn_701803f7","enter":"Toast_enter_701803f7"};

function determineHeight(entries, offset) {
    var height = 0;
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var entry = entries_1[_i];
        height += entry.contentRect.height;
    }
    return height + offset;
}
var useObserveHeight = function (offset) {
    if (offset === void 0) { offset = 0; }
    var toastRef = React.useRef(null);
    var _a = React.useState(0), toastHeight = _a[0], setToastHeight = _a[1];
    React.useEffect(function () {
        var resizeObserver = new ResizeObserver(function (entries) {
            setToastHeight(determineHeight(entries, offset));
        });
        var toast = toastRef.current;
        if (toast) {
            resizeObserver.observe(toast);
        }
        return function () {
            if (toast) {
                resizeObserver.unobserve(toast);
            }
        };
    }, [offset]);
    return {
        toastHeight: toastHeight,
        toastRef: toastRef,
    };
};

function Toast(_a) {
    var className = _a.className, contentClassName = _a.contentClassName, message = _a.message, id = _a.id, subText = _a.subText, ctas = _a.ctas, removeDelayProp = _a.removeDelay, combination = _a.combination, combinations = _a.combinations, removeToast = _a.removeToast, registerHeight = _a.registerHeight, _b = _a.variant, variant = _b === void 0 ? "info" : _b, transition = _a.transition, stackHeight = _a.stackHeight, removed = _a.removed, autoCloseCta = _a.autoCloseCta, icon = _a.icon;
    var _c = useObserveHeight(16), toastRef = _c.toastRef, toastHeight = _c.toastHeight;
    var _d = useState(), removeCallback = _d[0], setRemoveCallback = _d[1];
    var prevRemoveCallback = useRef();
    var _e = useState(stackHeight), constantStackHeight = _e[0], setConstantStackHeight = _e[1];
    var removeDelay = useMemo(function () {
        if (removeDelayProp)
            return removeDelayProp;
        return (ctas === null || ctas === void 0 ? void 0 : ctas.length) ? 6000 : 4000;
    }, [ctas === null || ctas === void 0 ? void 0 : ctas.length, removeDelayProp]);
    useEffect(function () {
        registerHeight(id, toastHeight);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toastHeight]);
    useEffect(function () {
        if (!removed) {
            setConstantStackHeight(stackHeight);
        }
    }, [stackHeight, removed]);
    useEffect(function () {
        if (!(ctas === null || ctas === void 0 ? void 0 : ctas.length) ||
            (!(ctas === null || ctas === void 0 ? void 0 : ctas.length) && (combinations === null || combinations === void 0 ? void 0 : combinations.length)) ||
            autoCloseCta) {
            var timeout_1 = window.setTimeout(function () {
                removeToast(id);
            }, removeDelay);
            setRemoveCallback(timeout_1);
            return function () { return window.clearTimeout(timeout_1); };
        }
    }, [
        id,
        ctas === null || ctas === void 0 ? void 0 : ctas.length,
        removeToast,
        combinations === null || combinations === void 0 ? void 0 : combinations.length,
        removeDelay,
        autoCloseCta,
    ]);
    useEffect(function () {
        if (prevRemoveCallback.current) {
            window.clearTimeout(prevRemoveCallback.current);
        }
        prevRemoveCallback.current = removeCallback;
    }, [removeCallback]);
    var size = useMemo(function () {
        var accumSize = 1;
        if (subText)
            accumSize += 1;
        if (ctas === null || ctas === void 0 ? void 0 : ctas.length)
            accumSize += 1;
        switch (accumSize) {
            case 3:
                return "large";
            case 2:
                return "medium";
            default:
                return "small";
        }
    }, [ctas === null || ctas === void 0 ? void 0 : ctas.length, subText]);
    var onCtaClick = useCallback(function (cb) {
        if (!cb || (cb && cb())) {
            removeToast(id);
        }
    }, [id, removeToast]);
    var onMouseEnter = useCallback(function () {
        window.clearTimeout(removeCallback);
    }, [removeCallback]);
    var onMouseLeave = useCallback(function () {
        var timeout = window.setTimeout(function () {
            removeToast(id);
        }, removeDelay);
        setRemoveCallback(timeout);
    }, [id, removeDelay, removeToast]);
    var getDefaultIcon = function (toastVariant) {
        switch (toastVariant) {
            case "error":
                return React__default.createElement(CircleAlertIcon, null);
            case "info":
            case "warning":
            case "action":
            case "success":
                return React__default.createElement(InfoIcon, null);
        }
    };
    return (React__default.createElement(StackingContextProvider, { className: styles.container },
        React__default.createElement("div", { className: classNames(styles.toastWrapper, className, styles[size], styles[variant]), style: {
                transform: "translateY(".concat(constantStackHeight, "px)"),
            }, ref: toastRef, "data-testid": "toast-".concat(variant) },
            React__default.createElement(animated.div, { className: styles.toast, style: transition, role: "alert" },
                React__default.createElement("div", { className: classNames(styles.content, contentClassName), onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
                    React__default.createElement("div", { className: styles.body },
                        React__default.createElement("div", { className: styles.icon }, icon || getDefaultIcon(variant)),
                        React__default.createElement("strong", { className: styles.title }, combination && (combinations === null || combinations === void 0 ? void 0 : combinations.length)
                            ? combination.message(combinations.length + 1)
                            : message),
                        subText && (React__default.createElement(Typography, { variant: "small", as: "p", className: styles.subText }, combination && (combinations === null || combinations === void 0 ? void 0 : combinations.length)
                            ? __spreadArray([subText], combinations.map(function (c) { return c.subText; }), true).join(combination.subTextSeperator || " & ")
                            : subText)),
                        (ctas === null || ctas === void 0 ? void 0 : ctas.length) && (React__default.createElement("div", { className: styles.ctasContainer }, ctas.map(function (cta, ctaIndex) {
                            var _a;
                            return (React__default.createElement("button", { key: ctaIndex, className: classNames(textLinkStyles, styles.cta, (_a = {},
                                    _a[styles.primary] = cta.primary,
                                    _a)), onClick: function () { return onCtaClick(cta.onClick); } }, cta.text));
                        }))))),
                React__default.createElement(IconButton, { label: "Close", "data-testid": "toast-close-btn", onClick: function () { return removeToast(id); }, className: styles.closeBtn },
                    React__default.createElement(CloseIcon, null))))));
}

var ActionTypes;
(function (ActionTypes) {
    ActionTypes[ActionTypes["ADD_TOAST"] = 0] = "ADD_TOAST";
    ActionTypes[ActionTypes["REMOVE_TOAST"] = 1] = "REMOVE_TOAST";
    ActionTypes[ActionTypes["REGISTER_HEIGHT"] = 2] = "REGISTER_HEIGHT";
})(ActionTypes || (ActionTypes = {}));

var defaultState = {
    toasts: [],
    heights: {},
};
var reducer = function (state, action) {
    var _a;
    var newHeights = __assign({}, state.heights);
    switch (action.type) {
        case ActionTypes.ADD_TOAST:
            if (action.payload.combine) {
                var existingToast = state.toasts.find(function (t) { var _a, _b; return ((_a = t.combine) === null || _a === void 0 ? void 0 : _a.type) === ((_b = action.payload.combine) === null || _b === void 0 ? void 0 : _b.type); });
                if (existingToast) {
                    existingToast.combinationToasts = __spreadArray(__spreadArray([], existingToast.combinationToasts, true), [
                        action.payload,
                    ], false);
                    return __assign(__assign({}, state), { toasts: __spreadArray([], state.toasts, true) });
                }
            }
            return __assign(__assign({}, state), { toasts: __spreadArray(__spreadArray([], state.toasts, true), [action.payload], false) });
        case ActionTypes.REMOVE_TOAST:
            delete newHeights[action.payload];
            return __assign(__assign({}, state), { toasts: state.toasts.filter(function (toast) { return toast.id !== action.payload; }), heights: newHeights });
        case ActionTypes.REGISTER_HEIGHT:
            return __assign(__assign({}, state), { heights: __assign(__assign({}, state.heights), (_a = {}, _a[action.payload.id] = action.payload.height, _a)) });
        default:
            return state;
    }
};

var ToastContext = React.createContext(null);
var toastCount = 0;
function createToast(info) {
    return __assign({ id: "toast-".concat(toastCount++), combinationToasts: [] }, info);
}
var ToastProvider = function (_a) {
    var children = _a.children;
    var Provider = ToastContext.Provider;
    var _b = React.useReducer(reducer, defaultState), state = _b[0], dispatch = _b[1];
    var addToast = React.useCallback(function (payload) {
        var info = typeof payload === "string"
            ? { message: payload, variant: "info" }
            : payload;
        var toastItem = createToast(info);
        dispatch({
            type: ActionTypes.ADD_TOAST,
            payload: toastItem,
        });
    }, [dispatch]);
    var removeToast = React.useCallback(function (id) {
        dispatch({
            type: ActionTypes.REMOVE_TOAST,
            payload: id,
        });
    }, [dispatch]);
    var registerHeight = React.useCallback(function (id, height) {
        if (!state.heights[id]) {
            dispatch({
                type: ActionTypes.REGISTER_HEIGHT,
                payload: { id: id, height: height },
            });
        }
    }, [dispatch, state.heights]);
    var transitions = useTransition(state.toasts, {
        from: {
            transform: "translateX(110%)",
        },
        enter: {
            transform: "translateX(0%)",
        },
        leave: {
            transform: "translateX(110%)",
        },
    });
    return (React.createElement(Provider, { value: { addToast: addToast, removeToast: removeToast } },
        children,
        transitions(function (transition, toast) {
            var keys = Object.keys(state.heights);
            var toastIndex = keys.indexOf(toast.id);
            var stackKeys = toastIndex === -1 ? keys : keys.slice(0, toastIndex);
            var stackHeight = Object.entries(state.heights).reduce(function (value, entry) {
                if (stackKeys.includes(entry[0])) {
                    value += entry[1];
                }
                return value;
            }, 0);
            return (React.createElement(Toast, __assign({ key: toast.id, transition: transition, removeToast: removeToast, registerHeight: registerHeight, stackHeight: stackHeight, removed: toastIndex === -1, combination: toast.combine, combinations: toast.combinationToasts }, toast)));
        })));
};

var OrchardContext = React__default.createContext(null);
var OrchardProvider = function (_a) {
    var children = _a.children, contextProps = __rest(_a, ["children"]);
    var Provider = OrchardContext.Provider;
    var suppressColorStyles = contextProps.suppressColorStyles, themeOverrides = contextProps.themeOverrides, colorsDivClassName = contextProps.colorsDivClassName, appId = contextProps.appId;
    return (React__default.createElement(Provider, { value: __assign({}, contextProps) },
        React__default.createElement(ToastProvider, null,
            React__default.createElement(ThemeProvider, { overrides: themeOverrides }, function (colorStyles) {
                var appliedColorStyles = suppressColorStyles ? {} : colorStyles;
                return (React__default.createElement(StackingContextProvider, { style: __assign({}, appliedColorStyles), className: colorsDivClassName, id: appId }, children));
            }))));
};

var useOrchard = function () {
    var context = React.useContext(OrchardContext);
    if (!context) {
        throw new Error("You must initialise an `OrchardProvider` before using Orchard modals or toasts");
    }
    return context;
};

export { OrchardProvider as O, StackingContextProvider as S, ToastProvider as T, useStackingContext as a, ToastContext as b, Toast as c, OrchardPortal as d, useOrchard as u };
